// DohcOverlay.jsx
import React, { useMemo } from "react";
import { Polygon, Polyline, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { renderToStaticMarkup } from "react-dom/server";

/** ───────────── 상태별 컬러 ───────────── */
const STATUS_COLORS = {
    Clear: "#2F8E20",
    Caution: "#FFC000",
    "No Access": "#D63F24",
};

/** ───────────── SVG 라벨 아이콘 ───────────── */
const DOHCSymbol = ({ label = "0", color = "#2F8E20" }) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        version="1.2"
        baseProfile="tiny"
        xmlSpace="preserve"
        style={{ shapeRendering: "geometricPrecision", fillRule: "evenodd" }}
        width="42"
        height="42"
        viewBox="0 0 42 42"
    >
        <style>{`
      .sl { stroke-linecap: round; stroke-linejoin: round; }
      .f0 { fill: none; }
      .sCHBLK { stroke: #000000; }
      .fWHITE { fill: #FFFFFF; }
      .fBLACK { fill: #000000; }
      .labelText {
        font-family: Arial, sans-serif;
        font-size: 3.5px;
        fill: black;
        text-anchor: middle;
        dominant-baseline: middle;
      }
    `}</style>
        {/* 42x42 기준 가운데(21,21)에 3배 스케일 */}
        <g transform="translate(21,21) scale(3)">
            <circle cx="0" cy="0" r="6.8" fill={color} />
            <circle cx="0" cy="0" r="6.8" className="sl f0 sCHBLK" style={{ strokeWidth: 0.32 }} />
            <circle cx="0" cy="0" r="5" className="fWHITE" />
            <circle cx="0" cy="0" r="5" className="sl f0 sCHBLK" style={{ strokeWidth: 0.32 }} />
            <text className="labelText" x="0" y="0.5">
                {Number.isFinite(+label) ? (+label).toFixed(1) : String(label ?? "")}
            </text>
            <path d="M -1,4.5 L -1,3.5 L -2,3.5 L 0,2.5 L 2,3.5 L 1,3.5 L 1,4.5 L -1,4.5 Z" className="fBLACK" />
            <path d="M -1,-4.5 L -1,-3.5 L -2,-3.5 L 0,-2.5 L 2,-3.5 L 1,-3.5 L 1,-4.5 L -1,-4.5 Z" className="fBLACK" />
        </g>
    </svg>
);

const makeIcon = (label, color) => {
    const svg = encodeURIComponent(renderToStaticMarkup(<DOHCSymbol label={label} color={color} />));
    return L.divIcon({
        html: `<img src="data:image/svg+xml,${svg}" style="width:42px;height:42px;" />`,
        iconSize: [42, 42],
        iconAnchor: [21, 21],
        className: "",
    });
};

/** ───────────── 유틸 ───────────── */
function toLatLngTuple(p) {
    if (Array.isArray(p) && p.length >= 2) {
        const a = Number(p[0]), b = Number(p[1]);
        if (Number.isFinite(a) && Math.abs(a) <= 90 && Number.isFinite(b) && Math.abs(b) <= 180) return [a, b]; // [lat,lng]
        if (Number.isFinite(a) && Math.abs(a) <= 180 && Number.isFinite(b) && Math.abs(b) <= 90) return [b, a]; // [lng,lat] -> [lat,lng]
    } else if (p && typeof p === "object") {
        const lat = Number(p.lat ?? p.latitude ?? p.y);
        const lng = Number(p.lng ?? p.lon ?? p.long ?? p.longitude ?? p.x);
        if (Number.isFinite(lat) && Math.abs(lat) <= 90 && Number.isFinite(lng) && Math.abs(lng) <= 180) return [lat, lng];
    }
    return null;
}

function extractCoordsFromGeoJSON(geo) {
    if (!geo || typeof geo !== "object" || !geo.type) return null;
    if (geo.type === "Polygon") return geo.coordinates?.[0] ?? null;     // [[lng,lat], ...]
    if (geo.type === "LineString") return geo.coordinates ?? null;       // [[lng,lat], ...]
    return null;
}

function getPolygonCentroid(points) {
    // points: [[lat,lng], ...], 최소 3개
    let area = 0, x = 0, y = 0;
    for (let i = 0; i < points.length; i++) {
        const [lat1, lng1] = points[i];
        const [lat2, lng2] = points[(i + 1) % points.length];
        const f = lng1 * lat2 - lng2 * lat1;
        area += f;
        x += (lng1 + lng2) * f;
        y += (lat1 + lat2) * f;
    }
    area /= 2;
    if (!area) return points[0];
    x /= (6 * area);
    y /= (6 * area);
    return [y, x]; // [lat,lng]
}

function destinationPoint([lat, lng], distance_m, bearing_deg) {
    const R = 6371000;
    const δ = distance_m / R;
    const θ = (bearing_deg * Math.PI) / 180;
    const φ1 = (lat * Math.PI) / 180;
    const λ1 = (lng * Math.PI) / 180;

    const sinφ1 = Math.sin(φ1), cosφ1 = Math.cos(φ1);
    const sinδ = Math.sin(δ), cosδ = Math.cos(δ);
    const sinθ = Math.sin(θ), cosθ = Math.cos(θ);

    const sinφ2 = sinφ1 * cosδ + cosφ1 * sinδ * cosθ;
    const φ2 = Math.asin(sinφ2);
    const y = sinθ * sinδ * cosφ1;
    const x = cosδ - sinφ1 * sinφ2;
    const λ2 = λ1 + Math.atan2(y, x);

    const lat2 = (φ2 * 180) / Math.PI;
    const lng2 = ((λ2 * 180) / Math.PI + 540) % 360 - 180;
    return [lat2, lng2];
}

/**
 * props:
 *  - geometry: GeoJSON Polygon/LineString 또는 좌표배열(폴리곤/라인)
 *  - dohc_status: "Clear" | "Caution" | "No Access"
 *  - label: 심볼 내부 라벨 텍스트(숫자면 소수1자리)
 *  - centerLat, centerLng: 심볼 중심 좌표(없으면 폴리곤/원/라인 기반 자동)
 *  - circle_points_wgs84: "lng lat lng lat ..." 문자열/배열/GeoJSON
 *  - structure_proximity_radius: 반지름(m) (라벨 기본값)
 *  - radius_m: 원 좌표가 없을 때 사용할 반경 값(미지정 시 structure_proximity_radius)
 *  - radiusBearingDeg: 반지름 방향(0:북, 90:동)
 *  - showCenter, showRadius, radiusColor, showRadiusLabel, radiusLabel, labelDigits
 *  - strokeColor, fillColor, fillOpacity, weight
 */
export default function DohcOverlay({
    data,
    geometry,
    dohc_status = "Clear",
    label = "0",
    centerLat,
    centerLng,
    circle_points_wgs84,
    structure_proximity_radius,

    radius_m,
    radiusBearingDeg = 90,
    showCenter = true,
    showRadius = true,
    radiusColor = "#E38039",
    showRadiusLabel = true,
    radiusLabel,
    labelDigits = 0,

    strokeColor,
    fillColor,
    fillOpacity = 0.5,
    weight = 2,
}) {
    const baseColor = STATUS_COLORS[dohc_status] || STATUS_COLORS.Clear;
    const lineColor = strokeColor || baseColor;
    const areaColor = fillColor || baseColor;

    // ── 폴리곤/폴리라인 정규화
    const poly = useMemo(() => {
        let raw = geometry;
        if (!raw) return { positions: [], isPolygon: false };
        if (raw && typeof raw === "object" && raw.type) raw = extractCoordsFromGeoJSON(raw) || [];
        const norm = Array.isArray(raw) ? raw.map(toLatLngTuple).filter(Boolean) : [];
        if (norm.length < 2) return { positions: [], isPolygon: false };
        const first = norm[0], last = norm[norm.length - 1];
        const isPoly = (first[0] === last[0] && first[1] === last[1]) || norm.length >= 3;
        if (isPoly && (first[0] !== last[0] || first[1] !== last[1])) norm.push([...first]);
        return { positions: norm, isPolygon: isPoly };
    }, [geometry]);

    // ── 원(선회반경) 좌표
    const circlePositions = useMemo(() => {
        if (!circle_points_wgs84) return [];
        let raw = circle_points_wgs84;

        // GeoJSON
        if (raw && typeof raw === "object" && raw.type) raw = extractCoordsFromGeoJSON(raw) || [];

        // "lng lat lng lat ..." 문자열
        if (typeof raw === "string") {
            const nums = raw.trim().split(/[,\s]+/).map(Number).filter(Number.isFinite);
            const norm = [];
            for (let i = 0; i < nums.length - 1; i += 2) {
                const lng = nums[i], lat = nums[i + 1];
                if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) norm.push([lat, lng]);
            }
            if (norm.length >= 3) {
                const [fLat, fLng] = norm[0], [lLat, lLng] = norm[norm.length - 1];
                if (fLat !== lLat || fLng !== lLng) norm.push([fLat, fLng]);
                return norm;
            }
            return [];
        }

        // 납작 숫자 배열 [lng,lat,...]
        if (Array.isArray(raw) && raw.length && raw.every((v) => typeof v === "number")) {
            const nums = raw.map(Number);
            const norm = [];
            for (let i = 0; i < nums.length - 1; i += 2) {
                const lng = nums[i], lat = nums[i + 1];
                if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) norm.push([lat, lng]);
            }
            if (norm.length >= 3) {
                const [fLat, fLng] = norm[0], [lLat, lLng] = norm[norm.length - 1];
                if (fLat !== lLat || fLng !== lLng) norm.push([fLat, fLng]);
                return norm;
            }
            return [];
        }

        // [[lat,lng]] / [{lat,lng}] 등
        if (Array.isArray(raw)) {
            const norm = raw.map(toLatLngTuple).filter(Boolean);
            if (norm.length >= 3) {
                const [fLat, fLng] = norm[0], [lLat, lLng] = norm[norm.length - 1];
                if (fLat !== lLat || fLng !== lLng) norm.push([fLat, fLng]);
                return norm;
            }
        }
        return [];
    }, [circle_points_wgs84]);

    // ── 중심 계산
    const center = useMemo(() => {
        // 1) 외부에서 명시된 center 우선
        if (Number.isFinite(centerLat) && Number.isFinite(centerLng)) {
            return [Number(centerLat), Number(centerLng)];
        }
        // 2) 폴리곤이면 폴리곤의 기하학적 중심
        if (poly.isPolygon && poly.positions.length >= 3) {
            return getPolygonCentroid(poly.positions);
        }
        // 3) 폴리라인(특히 2점)일 때: 첫 점과 끝 점의 기하학적 중점
        if (!poly.isPolygon && poly.positions.length >= 2) {
            const first = poly.positions[0];
            const last = poly.positions[poly.positions.length - 1];
            return [(first[0] + last[0]) / 2, (first[1] + last[1]) / 2];
        }
        // 4) 폴리곤이 없어도 원 좌표가 있으면 원의 중심
        if (circlePositions.length >= 3) {
            return getPolygonCentroid(circlePositions);
        }
        return null;
    }, [centerLat, centerLng, poly, circlePositions]);

    // ── 반지름 선/라벨 (원 좌표 있으면 1번째 점 사용, 없으면 지오데식)
    const radiusLine = useMemo(() => {
        if (!center || !showRadius) return null;

        if (Array.isArray(circlePositions) && circlePositions.length >= 2) {
            const tip = circlePositions[0];
            const mid = [(center[0] + tip[0]) / 2, (center[1] + tip[1]) / 2];
            const value =
                Number.isFinite(structure_proximity_radius) ? Number(structure_proximity_radius)
                    : Number.isFinite(radius_m) ? Number(radius_m)
                        : NaN;
            return { p1: center, p2: tip, mid, value_m: value };
        }

        const r =
            Number.isFinite(radius_m) ? Number(radius_m)
                : Number.isFinite(structure_proximity_radius) ? Number(structure_proximity_radius)
                    : NaN;
        if (!Number.isFinite(r) || r <= 0) return null;

        const tip = destinationPoint(center, r, radiusBearingDeg);
        const mid = [(center[0] + tip[0]) / 2, (center[1] + tip[1]) / 2];
        return { p1: center, p2: tip, mid, value_m: r };
    }, [center, showRadius, circlePositions, structure_proximity_radius, radius_m, radiusBearingDeg]);

    const radiusText = useMemo(() => {
        if (typeof radiusLabel === "string") return radiusLabel;
        const v = Number.isFinite(radiusLine?.value_m) ? Number(radiusLine.value_m) : NaN;
        if (!Number.isFinite(v)) return "";
        if (v >= 1000) return `${(v / 1000).toFixed(labelDigits)} km`;
        return `${v.toFixed(labelDigits)} m`;
    }, [radiusLine, radiusLabel, labelDigits]);

    const icon = useMemo(() => makeIcon(String(label ?? ""), baseColor), [label, baseColor]);

    const formatNum = (v, digits = 6) => {
        const n = typeof v === "number" ? v : Number(v);
        return Number.isFinite(n) ? n.toFixed(digits) : String(v ?? "");
    };

    const InfoRow = ({ label, value }) => {
        const display = typeof value === "string" ? value.trim() : value;

        const isEmpty =
            display == null ||
            (typeof display === "string" &&
                (display === "" || /^\(\s*\)$/.test(display)));

        if (isEmpty) return null;

        return (
            <div>
                <span style={{ color: "#666", fontSize: 16 }}>{label}: </span>
                <span style={{ fontSize: 16 }}>{display}</span>
            </div>
        );
    };

    // console.log(data);
    return (
        <>
            {/* 폴리곤/폴리라인 */}
            {poly.positions.length >= (poly.isPolygon ? 3 : 2) &&
                (poly.isPolygon ? (
                    <Polygon positions={poly.positions} pathOptions={{ color: lineColor, fillColor: areaColor, fillOpacity, weight }} />
                ) : (
                    <Polyline positions={poly.positions} pathOptions={{ color: lineColor, weight }} />
                ))}

            {/* 중심 심볼 + 팝업 (폴리라인일 때도 중앙에 표시) */}
            {center && showCenter && (
                <Marker position={center} icon={icon}>
                    <Popup>
                        <div style={{ minWidth: 160 }}>
                            <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 16 }}>
                                {`${data.name_ko} (${data.name_en})`}
                            </div>
                            <InfoRow label="세부이름" value={`${data.sub_name_ko} (${data.sub_name_en})`} />
                            <InfoRow label="타입" value={data.facility_type} />
                            <InfoRow label="위도 (1)" value={`${data.latitude1} (${formatNum(data.lat_dd1)})`} />
                            <InfoRow label="경도 (1)" value={`${data.longitude1} (${formatNum(data.lng_dd1)})`} />
                            <InfoRow label="위도 (2)" value={`${data.latitude2} (${formatNum(data.lat_dd2)})`} />
                            <InfoRow label="경도 (2)" value={`${data.longitude2} (${formatNum(data.lng_dd2)})`} />
                            {data?.facility_type === "Bridge" && (<>
                                <InfoRow label="위도 (3)" value={`${data.latitude3} (${formatNum(data.lat_dd3)})`} />
                                <InfoRow label="경도 (3)" value={`${data.longitude3} (${formatNum(data.lng_dd3)})`} />
                                <InfoRow label="위도 (4)" value={`${data.latitude4} (${formatNum(data.lat_dd4)})`} />
                                <InfoRow label="경도 (4)" value={`${data.longitude4} (${formatNum(data.lng_dd4)})`} />
                            </>)}
                            <InfoRow label="통과시간" value={data.passage_time} />
                            <InfoRow label="DOHC 값" value={`${formatNum(data.dohc, 1)}m`} />
                            <InfoRow label="DOHC 상태" value={data.dohc_status} />
                            <InfoRow label="Clear" value={`${data.clear}m 초과 통과`} />
                            <InfoRow label="Caution" value={`${data.no_access}m 이상 ${data.caution}m 이하 경고`} />
                            <InfoRow label="No Access" value={`${data.no_access}m 미만 불가`} />
                            <InfoRow label="구조물 높이" value={`${data.structure_height}m`} />
                            <InfoRow label="조위" value={`${formatNum(data.level_m, 2)}m`} />
                            <InfoRow label="수위" value={`${formatNum(data.water_level_height, 1)}m`} />
                            <InfoRow label="Air Gap" value={`${formatNum(data.air_gap, 1)}m`} />
                            <InfoRow label="Air Draft" value={`${data.air_draft}m`} />
                            <InfoRow label="반경" value={`${data.structure_proximity_radius}m`} />
                        </div>
                    </Popup>
                </Marker>
            )}

            {/* 원(선회반경) 폴리곤 */}
            {circlePositions.length >= 3 && (
                <Polygon
                    positions={circlePositions}
                    pathOptions={{ color: radiusColor, fillColor: radiusColor, fillOpacity: 0.12, weight: 2 }}
                />
            )}

            {/* 반지름 선 + 라벨 */}
            {radiusLine && (
                <>
                    <Polyline positions={[radiusLine.p1, radiusLine.p2]} pathOptions={{ color: radiusColor, weight: 2, dashArray: "4,4" }} />
                    {showRadiusLabel && radiusText && (
                        <Marker
                            position={radiusLine.mid}
                            icon={L.divIcon({
                                html: `<div style="font-size:12px;font-weight:700;color:${radiusColor};background:transparent;transform:translateY(8px);white-space:nowrap;text-shadow:0 0 1px rgba(255,255,255,0.7);">${radiusText}</div>`,
                                className: "",
                                iconSize: [1, 1],
                                iconAnchor: [0, 0],
                            })}
                        />
                    )}
                </>
            )}
        </>
    );
}
